import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw } from 'lucide-react';

// Signature pad — two modes:
//   • Draw: pointer/touch drawing (great on phones)
//   • Type: type your name, rendered in a signature script (great on desktop, where
//     drawing with a mouse is impractical)
// Defaults to Type on a fine pointer (mouse), Draw on a coarse pointer (touch).
const SIG_FONT = 'italic 600 SIZEpx "Snell Roundhand", "Segoe Script", "Brush Script MT", cursive';

export function SignaturePad({ onChange, defaultName }) {
  const prefersDraw = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const [mode, setMode] = useState(prefersDraw ? 'draw' : 'type');
  const [typed, setTyped] = useState(defaultName || '');
  const drawRef = useRef(null);
  const typeRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  // init draw canvas
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = drawRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111';
    dirty.current = false;
  }, [mode]);

  // render typed signature → PNG
  useEffect(() => {
    if (mode !== 'type') return;
    const canvas = typeRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const name = (typed || '').trim();
    if (!name) { onChange(''); return; }
    ctx.fillStyle = '#111';
    ctx.textBaseline = 'middle';
    let size = 46;
    const setFont = () => { ctx.font = SIG_FONT.replace('SIZE', size); };
    setFont();
    while (ctx.measureText(name).width > rect.width - 24 && size > 16) { size -= 2; setFont(); }
    ctx.fillText(name, 14, rect.height / 2 + 4);
    onChange(canvas.toDataURL('image/png'));
  }, [mode, typed, onChange]);

  const pos = (e) => { const r = drawRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = drawRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = drawRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); dirty.current = true; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current && onChange) onChange(drawRef.current.toDataURL('image/png')); };
  const clearDraw = () => { const c = drawRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirty.current = false; if (onChange) onChange(''); };

  const tabBtn = (m, label) => (
    <button type="button" onClick={() => setMode(m)} style={{
      padding: '4px 12px', borderRadius: 6, border: '1px solid var(--hp-border)', cursor: 'pointer',
      fontSize: '0.74rem', fontWeight: 700,
      background: mode === m ? 'var(--hp-gold)' : 'transparent', color: mode === m ? '#000' : 'var(--hp-dim)',
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {tabBtn('type', 'Type')}
        {tabBtn('draw', 'Draw')}
      </div>

      {mode === 'draw' ? (
        <>
          <canvas
            ref={drawRef}
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
            style={{ width: '100%', height: 160, background: '#fff', borderRadius: 8, border: '1px solid var(--hp-border)', touchAction: 'none', cursor: 'crosshair', display: 'block' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--hp-muted)' }}>Sign above with your finger or mouse</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearDraw}><RotateCcw size={12} /> Clear</button>
          </div>
        </>
      ) : (
        <>
          <input
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder="Type your full name"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box' }}
          />
          <canvas ref={typeRef} style={{ width: '100%', height: 90, background: '#fff', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block', marginTop: 8 }} />
          <span style={{ fontSize: '0.72rem', color: 'var(--hp-muted)' }}>Your typed name becomes your signature.</span>
        </>
      )}
    </div>
  );
}
